import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MemberAuthProvider, useMemberAuth } from './context/MemberAuthContext';
import ScrollToTop from './components/ui/ScrollToTop';

// ── Public pages ──────────────────────────────────────────────
import HomePage    from './pages/public/HomePage';
import EventsPage  from './pages/public/EventsPage';
import EventDetailPage from './pages/public/EventDetailPage';
import GalleryPage from './pages/public/GalleryPage';
import RadioPage   from './pages/public/RadioPage';
import LibraryPage from './pages/public/LibraryPage';
import IslamiyahPage from './pages/public/IslamiyahPage';
import AnnouncementsPage from './pages/public/AnnouncementsPage';
import { SawiaPage, QuotesPage, AboutPage } from './pages/public/OtherPages';
import { ContactPage } from './pages/public/ContactPage';

// ── Member auth ───────────────────────────────────────────────
import MemberRegisterPage from './pages/member/RegisterPage';
import {
  MemberLoginPage, VerifyEmailPage,
  ForgotPasswordPage, ResetPasswordPage,
} from './pages/member/AuthPages';

// ── Member dashboard ──────────────────────────────────────────
import {
  MemberLayout, MemberDashboardHome,
  MemberProfilePage, DhikrPage, MemberDirectoryPage,
} from './pages/member/MemberDashboard';

// ── Admin pages ───────────────────────────────────────────────
import LoginPage     from './pages/admin/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import { AdminEventsPage, AdminEventFormPage } from './pages/admin/AdminEventsPage';
import {
  AdminQuotesPage, AdminRadioPage,
  AdminAnnouncementsPage, AdminUsersPage, AdminGalleryPage,
} from './pages/admin/AdminOtherPages';
import AdminSawiaPage    from './pages/admin/AdminSawiaPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminTerrestrialPage from './pages/admin/AdminTerrestrialPage';
import AdminLibraryPage  from './pages/admin/AdminLibraryPage';
import AdminIslamiyahPage from './pages/admin/AdminIslamiyahPage';
import { AdminMembersPage, AdminMessagesPage } from './pages/admin/AdminMembersPage';
import { AdminForgotPasswordPage, AdminResetPasswordPage } from './pages/admin/AdminForgotPasswordPage';

// ── Route guards ──────────────────────────────────────────────
function RequireAdminAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/admin/login" replace />;
  if (roles && user.role !== 'super_admin' && !roles.includes(user.role))
    return <Navigate to="/admin" replace />;
  return children;
}

function RequireMemberAuth({ children }) {
  const { member, loading } = useMemberAuth();
  if (loading) return <PageLoader />;
  if (!member) return <Navigate to="/member/login" replace />;
  return children;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="h-12 w-12 rounded-full border-4 border-cyan-400 border-t-transparent animate-spin mx-auto mb-4" />
        <p className="text-navy-600 text-sm font-medium">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MemberAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>

            {/* ── PUBLIC ─────────────────────────────────── */}
            <Route path="/"              element={<HomePage />}           />
            <Route path="/events"        element={<EventsPage />}         />
            <Route path="/events/:slug"  element={<EventDetailPage />}    />
            <Route path="/gallery"       element={<GalleryPage />}        />
            <Route path="/radio"         element={<RadioPage />}          />
            <Route path="/library"       element={<LibraryPage />}        />
            <Route path="/islamiyah"     element={<IslamiyahPage />}      />
            <Route path="/sawia"         element={<SawiaPage />}          />
            <Route path="/quotes"        element={<QuotesPage />}         />
            <Route path="/about"         element={<AboutPage />}          />
            <Route path="/contact"       element={<ContactPage />}        />
            <Route path="/announcements" element={<AnnouncementsPage />}  />

            {/* ── MEMBER AUTH ────────────────────────────── */}
            <Route path="/member/register"        element={<MemberRegisterPage />} />
            <Route path="/member/login"           element={<MemberLoginPage />}    />
            <Route path="/verify-email"           element={<VerifyEmailPage />}    />
            <Route path="/member/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/member/reset-password"  element={<ResetPasswordPage />}  />

            {/* ── MEMBER DASHBOARD ───────────────────────── */}
            <Route path="/member/dashboard" element={
              <RequireMemberAuth><MemberLayout /></RequireMemberAuth>
            }>
              <Route index            element={<MemberDashboardHome />}  />
              <Route path="profile"   element={<MemberProfilePage />}    />
              <Route path="dhikr"     element={<DhikrPage />}            />
              <Route path="directory" element={<MemberDirectoryPage />}  />
            </Route>

            {/* ── ADMIN AUTH ─────────────────────────────── */}
            <Route path="/admin/login"           element={<LoginPage />}                  />
            <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />}    />
            <Route path="/admin/reset-password"  element={<AdminResetPasswordPage />}     />

            {/* ── ADMIN DASHBOARD ────────────────────────── */}
            <Route path="/admin" element={<RequireAdminAuth><DashboardPage /></RequireAdminAuth>} />
            <Route path="/admin/events"          element={<RequireAdminAuth><AdminEventsPage /></RequireAdminAuth>} />
            <Route path="/admin/events/new"      element={<RequireAdminAuth><AdminEventFormPage /></RequireAdminAuth>} />
            <Route path="/admin/events/:id/edit" element={<RequireAdminAuth><AdminEventFormPage /></RequireAdminAuth>} />
            <Route path="/admin/quotes"          element={<RequireAdminAuth><AdminQuotesPage /></RequireAdminAuth>} />
            <Route path="/admin/gallery"         element={<RequireAdminAuth><AdminGalleryPage /></RequireAdminAuth>} />
            <Route path="/admin/sawia"           element={<RequireAdminAuth><AdminSawiaPage /></RequireAdminAuth>} />
            <Route path="/admin/radio"           element={<RequireAdminAuth><AdminRadioPage /></RequireAdminAuth>} />
            <Route path="/admin/announcements"   element={<RequireAdminAuth><AdminAnnouncementsPage /></RequireAdminAuth>} />
            <Route path="/admin/members"         element={<RequireAdminAuth><AdminMembersPage /></RequireAdminAuth>} />
            <Route path="/admin/messages"        element={<RequireAdminAuth><AdminMessagesPage /></RequireAdminAuth>} />
            <Route path="/admin/users"           element={<RequireAdminAuth roles={['super_admin']}><AdminUsersPage /></RequireAdminAuth>} />
            <Route path="/admin/settings"        element={<RequireAdminAuth><AdminSettingsPage /></RequireAdminAuth>} />
            <Route path="/admin/terrestrial"     element={<RequireAdminAuth><AdminTerrestrialPage /></RequireAdminAuth>} />
            <Route path="/admin/library"         element={<RequireAdminAuth><AdminLibraryPage /></RequireAdminAuth>} />
            <Route path="/admin/islamiyah"       element={<RequireAdminAuth><AdminIslamiyahPage /></RequireAdminAuth>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>

          <ToastContainer
            position="top-right" autoClose={3500}
            hideProgressBar={false} closeOnClick pauseOnHover
            theme="colored"
            toastStyle={{ borderRadius: '12px', fontSize: '14px' }}
          />
        </BrowserRouter>
      </MemberAuthProvider>
    </AuthProvider>
  );
}

export default App;
